import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { Linking, ScrollView, View } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";
import Gap from "../../ui_atoms/Gap";
import { GET_WORKER_INFO, REMOVE_WORKER_FROM_CLIENT } from "../../graphql/core";
import { WorkerInfo, Document } from "../../__generated__/graphql";
import DocumentEditor from "../../components/document/DocumentEditor";
import ListItem from "../../ui_atoms/ListItem";
import UserAvatar from "../user/UserAvatar";
import ListHeader from "../../ui_atoms/ListHeader";
import ListTitle from "../../ui_atoms/ListTitle";
import { useNavigation } from "@react-navigation/native";
import { ClientWorkersTabParamList } from "../../navigations/client/WorkersNavigation";
import { useSnackbar } from "../../hooks/snackbar";
import { Alert } from "../../components/Alert";

const WorkerHeader: React.FC<{ workerInfo: WorkerInfo }> = ({ workerInfo }) => {
    return (
        <View style={{ width: "100%", alignItems: "center" }}>
            <UserAvatar avatarPath={workerInfo.avatarUrl} size={100} />
        </View>
    );
};

type ScreenParams = StackScreenProps<RootStackParamList & ClientWorkersTabParamList>;
type Props = StackScreenProps<ClientWorkersTabParamList, "WorkerDetails">;

const WorkerDetailsScreen: React.FC<Props> = ({ route }) => {
    const i18n = useI18n();
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const { loading, error, data } = useQuery(GET_WORKER_INFO, {
        variables: { workerId: route?.params?.workerId || "" },
        fetchPolicy: "cache-and-network",
    });
    const [removeWorkerFromClient, { loading: removing }] = useMutation(REMOVE_WORKER_FROM_CLIENT, {
        variables: { workerProfileId: route?.params?.workerId || "" },
    });
    const [document, setDocument] = useState<Document | null>(null);
    const workerInfo = useMemo(() => data?.getWorkerInfo, [data]);
    useEffect(() => {
        navigation.setOptions({
            title: workerInfo?.name || "",
        });
    }, [workerInfo, navigation]);
    const missionCount = useMemo(() => workerInfo?.missionCount?.total || 0, [workerInfo]);
    const { showSnackbar } = useSnackbar();

    const handleRemoveWorker = useCallback(() => {
        Alert.alert(
            i18n.t("worker_details.remove_worker_title"),
            i18n.t("worker_details.remove_worker_description"),
            [
                {
                    text: i18n.t("worker_details.remove_confirm"),
                    onPress: () => {
                        void removeWorkerFromClient().then(() => {
                            showSnackbar({
                                text: i18n.t(`worker_details.remove_worker_done`),
                                mode: "success",
                            });
                            navigation.popToTop();
                        });
                    },
                    style: "destructive",
                },
                {
                    text: i18n.t("worker_details.remove_cancel"),
                    style: "cancel",
                },
            ],
            { cancelable: true },
        );
    }, [i18n, navigation, removeWorkerFromClient, showSnackbar]);

    const canRemoveTalent = useMemo(() => {
        if (!workerInfo?.missionCount) {
            return true;
        }
        return (
            workerInfo.missionCount.accepted +
                workerInfo.missionCount.pendingAcceptation +
                workerInfo.missionCount.terminated ===
            0
        );
    }, [workerInfo?.missionCount]);

    return (
        <LoadingBoundary loading={loading && !data} error={error}>
            {workerInfo && (
                <ScrollView>
                    <VStack>
                        <Gap gap={1} />
                        <WorkerHeader workerInfo={workerInfo} />
                        <Gap gap={2} />
                        <ListTitle value={i18n.t("worker_details.title")} />
                        <ListItem
                            key="name"
                            avatarPath={workerInfo?.avatarUrl}
                            description={workerInfo.name}
                        />
                        <ListItem
                            key="missions"
                            icon="mission"
                            description={[
                                i18n.t("worker_details.mission_count", {
                                    count: missionCount,
                                }),
                                i18n.t("worker_details.years_of_experience", {
                                    count: workerInfo.yearsOfExperience || 0,
                                }),
                            ].join(" - ")}
                            onPress={() => {
                                navigation.push("WorkerMissionList", {
                                    workerId: workerInfo.id,
                                });
                            }}
                        />
                        <ListItem
                            key="phone"
                            icon="phone-call"
                            description={workerInfo.phoneNumber || ""}
                            onPress={() => {
                                if (workerInfo.phoneNumber) {
                                    void Linking.openURL(`tel:${workerInfo.phoneNumber}`);
                                }
                            }}
                            rightIcon="phone-call"
                            rightActionMode="action"
                        />

                        <ListItem
                            key="email"
                            icon="email"
                            description={workerInfo.email || ""}
                            onPress={() => {
                                if (workerInfo.email) {
                                    void Linking.openURL(`mailto:${workerInfo.email}`);
                                }
                            }}
                            rightIcon="external-link"
                            rightActionMode="action"
                        />
                        {!!workerInfo.documents?.length && (
                            <>
                                <Gap gap={1} />
                                <ListHeader value={i18n.t("worker_details.documents_title")} />
                                {workerInfo.documents.map((document) => {
                                    return (
                                        <ListItem
                                            key={document.id}
                                            icon="file"
                                            title={document.title}
                                            onPress={() => setDocument(document)}
                                        />
                                    );
                                })}
                            </>
                        )}
                        <Gap gap={1} />
                        <ListItem
                            labels={[
                                {
                                    text: i18n.t("worker_details.create_mission_action", {
                                        name: workerInfo.name,
                                    }),
                                    mode: "cta",
                                },
                            ]}
                            onPress={() => {
                                navigation.push("CreateMissionStack", { workerInfo });
                            }}
                            rightActionMode="action"
                        />
                        <ListItem
                            labels={[
                                {
                                    text: i18n.t("worker_details.list_missions_action", {
                                        name: workerInfo.name,
                                    }),
                                    mode: "cta",
                                },
                            ]}
                            onPress={() => {
                                navigation.push("WorkerMissionList", {
                                    workerId: workerInfo.id,
                                });
                            }}
                            rightActionMode="action"
                        />
                        <ListItem
                            labels={[
                                {
                                    text: i18n.t("worker_details.list_timesheet_action"),
                                    mode: "cta",
                                },
                            ]}
                            onPress={() => {
                                navigation.push("WorkerTimesheetList", {
                                    workerId: workerInfo.id,
                                });
                            }}
                            rightActionMode="action"
                        />
                        <ListItem
                            labels={[
                                {
                                    text: i18n.t("worker_details.list_invoices_action"),
                                    mode: "cta",
                                },
                            ]}
                            onPress={() => {
                                navigation.push("WorkerInvoices", {
                                    workerId: workerInfo.id,
                                });
                            }}
                            rightActionMode="action"
                        />
                        {canRemoveTalent && (
                            <ListItem
                                labels={[
                                    {
                                        text: i18n.t("worker_details.remove_worker_action"),
                                        mode: "redCta",
                                    },
                                ]}
                                onPress={handleRemoveWorker}
                                rightActionMode="empty"
                                disabled={removing}
                            />
                        )}
                    </VStack>
                    <Gap gap={2} />
                </ScrollView>
            )}
            {document && (
                <DocumentEditor
                    visible={!!document}
                    document={document}
                    onClose={() => {
                        setDocument(null);
                    }}
                />
            )}
        </LoadingBoundary>
    );
};

export default WorkerDetailsScreen;
